import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Instagram } from 'lucide-react';
import { Button } from '../components/ui/button';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigation = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Survey Search', path: '/survey-search' },
    { name: 'Property Hub', path: '/property-hub' },
    { name: 'Fee Scale', path: '/fee-scale' },
    { name: 'Consultation', path: '/consultation' },
    { name: 'Contact', path: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white/95 backdrop-blur-md shadow-md py-2' : 'bg-white/90 backdrop-blur-sm shadow-sm py-3'
    }`}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center transition-opacity duration-300 hover:opacity-80">
            <img 
              src="https://customer-assets.emergentagent.com/job_geopick-survey/artifacts/jqqgmzvv_IMG_3434.jpeg"
              alt="Versatile Geopick NIG Ltd"
              className="h-12 w-auto"
              style={{ filter: 'sepia(20%) saturate(80%) hue-rotate(-20deg)' }}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigation.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-5 py-2 rounded-full font-normal text-sm transition-all duration-300 ${
                  isActive(item.path)
                    ? 'bg-emerald-700 text-white'
                    : isScrolled
                    ? 'text-stone-700 hover:bg-stone-100'
                    : 'text-stone-800 hover:bg-white/20'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-4">
            <a 
              href="https://instagram.com/versatilegeopick"
              target="_blank"
              rel="noopener noreferrer"
              className={`p-2 rounded-full transition-colors duration-300 ${
                isScrolled ? 'text-stone-600 hover:text-emerald-700 hover:bg-stone-100' : 'text-stone-700 hover:bg-white/20'
              }`}
              aria-label="Instagram"
            >
              <Instagram className="h-5 w-5" />
            </a>
            <Button 
              size="sm"
              className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full px-6 transition-all duration-300 hover:shadow-lg"
              asChild
            >
              <a href="tel:+2348034494507">
                <Phone className="h-4 w-4 mr-2" />
                Call Us
              </a>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`lg:hidden p-2 rounded-full transition-colors duration-300 ${
              isScrolled ? 'text-stone-700 hover:bg-stone-100' : 'text-stone-800 hover:bg-white/20'
            }`}
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden mt-4 py-4 bg-white/95 backdrop-blur-md rounded-2xl border border-stone-200 animate-fade-in">
            <nav className="flex flex-col space-y-1 px-2">
              {navigation.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`px-4 py-3 rounded-xl font-normal transition-all duration-300 ${
                    isActive(item.path)
                      ? 'bg-emerald-700 text-white'
                      : 'text-stone-700 hover:bg-stone-100'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>
            <div className="mt-4 pt-4 border-t border-stone-200 px-4 space-y-3">
              <a 
                href="https://instagram.com/versatilegeopick"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-stone-600 hover:text-emerald-700 transition-colors duration-300"
              >
                <Instagram className="h-5 w-5" />
                <span>@versatilegeopick</span>
              </a>
              <Button 
                size="sm"
                className="w-full bg-emerald-700 hover:bg-emerald-800 text-white"
                asChild
              >
                <a href="tel:+2348034494507">
                  <Phone className="h-4 w-4 mr-2" />
                  +234 803 449 4507
                </a>
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
